import { TFunction } from 'i18next';
import moment from 'moment';
import { AccountData, PassSwapErrorCode, PassSwapExceptionResponse } from '../../../../api';
import { getPassOrderPage } from '../../../../utils/AccountUtils';

export type ResolvedSwapError = {
  code?: PassSwapErrorCode;
  message: string;
  providerError?: string;
};

const SWAP_ERROR_CODES = new Set<string>(Object.values(PassSwapErrorCode));

const getBody = (e: unknown): PassSwapExceptionResponse | undefined => {
  const body = (e as { body?: PassSwapExceptionResponse })?.body;
  return body && SWAP_ERROR_CODES.has(body.code) ? body : undefined;
};

/**
 * The swap exception carries ids only, so labels are resolved here from the account.
 * Unmapped codes fall back to `detail`, which the API writes as a finished admin-facing sentence.
 */
export const resolveSwapError = (
  e: unknown,
  t: TFunction,
  account: AccountData,
  language: 'en' | 'he',
  dateFormat: string,
): ResolvedSwapError => {
  const body = getBody(e);
  if (!body) return { message: t('saveHeader.errorGeneric') };

  const labelOf = (id?: string): string =>
    (id && getPassOrderPage(account, id)?.label?.[language]) || t('passes.pass_product');
  const dateOf = (value?: string): string => (value ? moment(value).format(dateFormat) : '');

  const message = t(`passes.swap.errors.${body.code}`, {
    defaultValue: body.detail,
    current: labelOf(body.current_pass_order_page_id),
    target: labelOf(body.target_pass_order_page_id),
    expected: body.expected_selection_count,
    provided: body.provided_selection_count,
    effectiveDate: dateOf(body.effective_date),
    nextChargeDate: dateOf(body.next_charge_date),
  });

  return { code: body.code, message, providerError: body.provider_error };
};
